import styles from "./OrdersOverview.module.css";

const STATUS_ORDER = ["ordered", "progress", "released"];
const EXCLUDED_STATUS_COUNTS = new Set(["delivered", "cancelled"]);
const CHART_COLORS = [
  "var(--primary-500)",
  "var(--yellow-500)",
  "var(--green-500)",
  "var(--violet-500)",
  "var(--red-500)",
  "var(--teal-500)",
  "var(--orange-500)",
];

const formatStatus = (status) => {
  if (!status) {
    return "Unknown";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const formatMonth = (month) => {
  if (!month) {
    return "Unknown";
  }

  const date = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return month;
  }

  return new Intl.DateTimeFormat("en", {
    month: "short",
    year: "2-digit",
  }).format(date);
};

const normalizeCount = (value) => Number(value || 0);

const sumCounts = (items) => items.reduce((total, item) => total + normalizeCount(item.count), 0);

function ChartCard({ title, subtitle, children }) {
  return (
    <section className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>{title}</h3>
        {subtitle ? <p className={styles.chartSubtitle}>{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

function PieChart({ title, subtitle, data }) {
  const total = sumCounts(data);
  let currentPercent = 0;

  const gradient = total
    ? data
        .map((item, index) => {
          const value = normalizeCount(item.count);
          const nextPercent = currentPercent + (value / total) * 100;
          const segment = `${CHART_COLORS[index % CHART_COLORS.length]} ${currentPercent}% ${nextPercent}%`;
          currentPercent = nextPercent;
          return segment;
        })
        .join(", ")
    : "var(--gray-200) 0% 100%";

  return (
    <ChartCard title={title} subtitle={subtitle}>
      {total ? (
        <div className={styles.pieContent}>
          <div
            className={styles.pie}
            style={{ background: `conic-gradient(${gradient})` }}
            aria-label={`${title}, total ${total}`}
          >
            <span className={styles.pieTotal}>{total}</span>
          </div>
          <ul className={styles.legend}>
            {data.map((item, index) => {
              const count = normalizeCount(item.count);
              const percent = Math.round((count / total) * 100);

              return (
                <li key={item.label} className={styles.legendItem}>
                  <span
                    className={styles.legendColor}
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span className={styles.legendLabel}>{item.label}</span>
                  <span className={styles.legendValue}>
                    {count} ({percent}%)
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <p className={styles.emptyState}>No orders to display.</p>
      )}
    </ChartCard>
  );
}

function BarChart({ title, subtitle, data }) {
  const maxCount = Math.max(...data.map((item) => normalizeCount(item.count)), 0);

  return (
    <ChartCard title={title} subtitle={subtitle}>
      {data.length ? (
        <div className={styles.barChart} aria-label={title}>
          {data.map((item) => {
            const count = normalizeCount(item.count);
            const height = maxCount ? `${Math.max((count / maxCount) * 100, 6)}%` : "0%";

            return (
              <div key={item.month} className={styles.barColumn}>
                <div className={styles.barTrack}>
                  <div className={styles.bar} style={{ height }}>
                    <span className={styles.barValue}>{count}</span>
                  </div>
                </div>
                <span className={styles.barLabel}>{formatMonth(item.month)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={styles.emptyState}>No delivered orders with delivery dates.</p>
      )}
    </ChartCard>
  );
}

function KpiCard({ title, value, description, valueTone = "default" }) {
  const valueClass =
    valueTone === "alert" ? `${styles.kpiValue} ${styles.kpiValueAlert}` : styles.kpiValue;
  return (
    <section className={styles.kpiCard}>
      <p className={styles.kpiLabel}>{title}</p>
      <p className={valueClass}>{value}</p>
      <p className={styles.kpiDescription}>{description}</p>
    </section>
  );
}

function OrdersOverview({ summary }) {
  const statusCounts = (summary?.statusCounts || [])
    .filter((item) => !EXCLUDED_STATUS_COUNTS.has(item.status))
    .sort((a, b) => {
      const statusAIndex = STATUS_ORDER.indexOf(a.status);
      const statusBIndex = STATUS_ORDER.indexOf(b.status);
      return (statusAIndex === -1 ? 999 : statusAIndex) - (statusBIndex === -1 ? 999 : statusBIndex);
    })
    .map((item) => ({
      label: formatStatus(item.status),
      count: item.count,
    }));

  const customerCounts = (summary?.customerCounts || []).map((item) => ({
    label: item.customerName,
    count: item.count,
  }));

  const activeCustomerCounts = (summary?.activeCustomerCounts || []).map((item) => ({
    label: item.customerName,
    count: item.count,
  }));

  return (
    <section className={styles.ordersModule}>
      <div className={styles.moduleHeader}>
        <div>
          <p className={styles.moduleEyebrow}>Module</p>
          <h2 className={styles.moduleTitle}>Orders</h2>
        </div>
        <p className={styles.moduleSummary}>
          Current order workload, customer split, delivered trend, and invoicing gaps.
        </p>
      </div>

      <div className={styles.metricsGrid}>
        <KpiCard
          title="Orders Without an Invoice"
          value={normalizeCount(summary?.ordersWithoutInvoiceCount)}
          description="Orders that have no invoice issued yet"
          valueTone="alert"
        />
        <KpiCard
          title="Delivered Orders"
          value={normalizeCount(summary?.deliveredCount)}
          description="Orders currently marked as delivered"
        />
        <PieChart
          title="Active Orders by Status"
          subtitle="Excludes delivered and cancelled"
          data={statusCounts}
        />
        <PieChart title="All Orders by Customer" subtitle="Includes every status" data={customerCounts} />
        <PieChart
          title="Active Orders by Customer"
          subtitle="Excludes delivered and cancelled"
          data={activeCustomerCounts}
        />
        <BarChart
          title="Delivered Orders by Month"
          subtitle="Grouped by delivery date"
          data={summary?.deliveredByMonth || []}
        />
      </div>
    </section>
  );
}

export default OrdersOverview;
